/**
 * web/src/screens/Notebook/giftflow/types.ts — the phase-2 gift flow's
 * client types, chip vocabularies and pure display helpers (card
 * NYJEJRCBfBGQ). The wire types come from shared/types (gifts.ts); the flow
 * adds only its own view/chip vocabulary here.
 */
import type { GiftBrief, GiftPlan, GiftSearch, GiftSearchRec } from '../../../../../shared/types';

export type { GiftBrief, GiftPlan, GiftSearch, GiftSearchRec };

/** home | the four steps | 'defaults' (the Gift-defaults editor, which reuses
 *  the Questions step). */
export type GiftFlowView = 'home' | 1 | 2 | 3 | 4 | 'defaults';

export const STEP_LABELS = ['Occasion', 'Questions', 'Ideas', 'Plan'] as const;
export type StepNo = 1 | 2 | 3 | 4;

export function stepNoOf(view: GiftFlowView): StepNo | null {
  return view === 1 || view === 2 || view === 3 || view === 4 ? view : null;
}

/** The occasionType key the giver's DEFAULTS brief hangs off — a real search
 *  row so it rides the same giver-private tables, filtered out of every
 *  home list by this constant. */
export const DEFAULTS_OCCASION = 'gift-defaults';

/* ── step-2 chip vocabularies (design: the founder's mockup) ─────────────── */

export const KIND_CHIPS: { value: string | null; label: string }[] = [
  { value: 'thing', label: 'Thing' },
  { value: 'experience', label: 'Experience' },
  { value: 'meal', label: 'Meal' },
  { value: 'clothing', label: 'Clothing' },
  { value: null, label: 'Surprise me' },
];

export const BUDGET_CHIPS: { label: string; min: number | null; max: number | null }[] = [
  { label: 'Under A$50', min: 0, max: 50 },
  { label: 'A$50–100', min: 50, max: 100 },
  { label: 'A$100–200', min: 100, max: 200 },
  { label: 'No limit', min: null, max: null },
];

export const VIBE_CHIPS: { value: string; label: string }[] = [
  { value: 'sentimental', label: 'Sentimental' },
  { value: 'playful', label: 'Playful' },
  { value: 'practical', label: 'Practical' },
];

/** `Not this` reason chips — each writes its avoid text into the brief. */
export const REJECT_REASONS: { value: string; label: string; avoid: string }[] = [
  { value: 'too_expensive', label: 'Too expensive', avoid: 'too expensive' },
  { value: 'not_her_style', label: 'Not her style', avoid: 'not her style' },
  { value: 'already_has_it', label: 'Already has it', avoid: 'already has it' },
  { value: 'other', label: 'Other', avoid: 'other (see brief)' },
];

export const PLAN_STATUS_LABELS: { value: 'to_acquire' | 'acquired' | 'given'; label: string }[] = [
  { value: 'to_acquire', label: 'To book' },
  { value: 'acquired', label: 'Booked' },
  { value: 'given', label: 'Given' },
];

/** The draft the Questions step edits. */
export interface BriefDraft {
  kind: string | null;
  budgetMin: number | null;
  budgetMax: number | null;
  vibe: string | null;
  freeText: string;
}

/* ── pure display helpers ────────────────────────────────────────────────── */

/** '2026-11-12' -> '12 Nov' (UTC-safe, en-AU like the rest of the app). */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function daysUntil(dateIso: string): number {
  const today = new Date();
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const target = Date.parse(`${dateIso}T00:00:00Z`);
  return Math.round((target - todayUtc) / 86_400_000);
}

/** '2026-11-12' minus n days, as YYYY-MM-DD. */
export function minusDays(dateIso: string, n: number): string {
  const d = new Date(`${dateIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

export function fmtDeadlineLine(deadline: string | null): string {
  return deadline ? `Book by ${fmtDate(deadline)}.` : '';
}

export function budgetLabel(min: number | null, max: number | null): string {
  if (min === null && max === null) return 'No limit';
  if (min !== null && max !== null) return min === 0 ? `Under A$${max}` : `A$${min}–A$${max}`;
  if (max !== null) return `Under A$${max}`;
  return `A$${min}+`;
}

export function kindLabel(kind: string | null): string {
  const hit = KIND_CHIPS.find((k) => k.value === kind);
  return hit ? hit.label : 'Surprise me';
}

/** Prefill a Questions draft from a brief (defaults or the search's own). */
export function draftFromBrief(b: GiftBrief | null): BriefDraft {
  const p = b?.parsed;
  return {
    kind: p?.kind ?? null,
    budgetMin: p?.budgetMin ?? null,
    budgetMax: p?.budgetMax ?? null,
    vibe: p?.vibe ?? null,
    freeText: b?.freeText ?? '',
  };
}

/** Prefill from the legacy gift defaults (preference + budgetMax) when no
 *  defaults brief exists yet. */
export function draftFromPrefs(preference: string | null, budgetMax: number | null): BriefDraft {
  const kind = preference === 'experience' || preference === 'clothing' || preference === 'meal'
    ? preference
    : null;
  let budgetMin: number | null = null;
  let budgetMaxOut: number | null = budgetMax;
  if (budgetMax !== null) {
    budgetMin = budgetMax <= 50 ? 0 : budgetMax <= 100 ? 50 : budgetMax <= 200 ? 100 : null;
    if (budgetMin === null) budgetMaxOut = null; // beyond the bands = no limit
  }
  return { kind, budgetMin, budgetMax: budgetMaxOut, vibe: null, freeText: '' };
}

/** The plan checklist for a picked rec, by kind (design: "checklist by source
 *  type" — every rec the fixture path can produce is an idea, so the first
 *  step is shaped by the brief's kind). Only {label, done} is stored; the
 *  sub-line and action are derived from the label on render. */
export function planStepsFor(kind: string | null, deadline: string | null): { label: string; done: boolean }[] {
  const bookBy = fmtDeadlineLine(deadline);
  const acquireLabel = kind === 'experience' ? 'Find and book a class' : 'Order it';
  void bookBy;
  return [
    { label: acquireLabel, done: false },
    { label: 'Remind me', done: false },
    { label: 'Write the card', done: false },
  ];
}

/** Sub-line + action for a plan step, derived from its label. */
export function planStepMeta(label: string, deadline: string | null): { sub: string; action: string } {
  const bookBy = fmtDeadlineLine(deadline);
  if (label === 'Find and book a class') return { sub: `I'll shortlist options near you.${bookBy ? ` ${bookBy}` : ''}`, action: 'Find' };
  if (label === 'Order it') return { sub: `I'll shortlist options that arrive in time.${bookBy ? ` ${bookBy}` : ''}`, action: 'Find' };
  if (label === 'Remind me') return { sub: 'Two days before the deadline', action: 'Add' };
  if (label === 'Write the card') return { sub: "I'll draft a note from what you shared", action: 'Draft' };
  return { sub: '', action: 'Add' };
}
